import { memo } from "react";
import { CopyPlusIcon, MailIcon } from "lucide-react";
import { toast } from "sonner";
import { Campaign, CampaignInbox, Creator } from "@/types/types";
import { ScopeWrapper } from "@/components/ui/scope-wrapper";

interface InboxColumnCellProps {
  email: string;
  provider: string;
}

/** A row's mailbox: the Amy-v2 mailbox stamped on the creator (the one that
 *  emails them — a campaign can hold several), else its campaign's. Mirrors
 *  INBOX_PATH in backend/services/org_creators.py (filter / sort / facet). */
export function creatorInbox(
  creator: Creator,
  campaign: Campaign | null | undefined
): CampaignInbox | undefined {
  if (creator.eve_email_address) return { email: creator.eve_email_address, provider: "amy" };
  return campaign?.inbox;
}

/** Provider mark for a sending mailbox: Amy's pool address, or the owner's Gmail / Outlook. */
export function InboxProviderIcon({
  provider,
  className = "size-4",
}: {
  provider: string;
  className?: string;
}) {
  if (provider === "amy") {
    return <img src="/amy.svg" alt="Amy" className={`${className} rounded-pill border bg-background`} />;
  }
  if (provider === "google") {
    return <img src="/google.svg" alt="Gmail" className={className} />;
  }
  if (provider === "microsoft") {
    return <img src="/microsoft.svg" alt="Outlook" className={className} />;
  }
  return <MailIcon className={`${className} text-muted-foreground`} />;
}

export function inboxProviderLabel(provider: string): string {
  if (provider === "amy") return "Amy's mailbox";
  if (provider === "google") return "Gmail";
  if (provider === "microsoft") return "Outlook";
  return "Mailbox";
}

/**
 * Global CRM only: the mailbox that emails the row's creator (creatorInbox).
 * Amy-v2's Composio address for Amy-run campaigns, else the owner's connected account.
 */
function InboxColumnCell({ email, provider }: InboxColumnCellProps) {
  if (!email) {
    return <span className="text-muted-foreground text-xs italic pl-2">unknown</span>;
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(email);
      toast.success("Mailbox address copied to clipboard!");
    } catch {
      toast.error("Couldn't copy the mailbox address");
    }
  };

  return (
    <ScopeWrapper className="max-w-full w-full">
      <button
        type="button"
        title={`Copy ${email}`}
        onClick={handleCopy}
        className="group flex h-10 w-full max-w-full items-center gap-2 rounded-md pl-2 pr-2 text-left hover:bg-muted-foreground/10 transition-colors cursor-pointer"
      >
        <InboxProviderIcon provider={provider} className="size-4 shrink-0" />
        <span className="flex min-w-0 flex-col items-start leading-tight">
          <span className="max-w-full truncate text-sm font-medium">{email}</span>
          <span className="max-w-full truncate font-mono text-[10px] uppercase text-muted-foreground">
            {inboxProviderLabel(provider)}
          </span>
        </span>
        <CopyPlusIcon className="ml-auto size-3 shrink-0 opacity-0 transition-none group-hover:scale-110 group-hover:opacity-100" />
      </button>
    </ScopeWrapper>
  );
}

export default memo(InboxColumnCell);